import { apiClient } from "@/lib/api-client";
import type { ReferralLinkResp, ReferredAccountsResp } from "@/types/referrals.ts";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

/**
 * The signed-in account's referral link, for web and native alike. The server
 * mints the code once and never changes it, so a fetched link never goes stale;
 * switching accounts reloads the app, which drops it with the rest of the cache.
 */
export const referralLinkQueryOptions = queryOptions({
  queryKey: ["referralLink"],
  queryFn: ({ signal }) =>
    apiClient.get<ReferralLinkResp>("/api/a/referrals/link", { signal }).then((res) => res.data),
  staleTime: Infinity,
});

export const REFERRED_ACCOUNTS_PAGE_SIZE = 10;

/** One page of the accounts that signed up through the signed-in account's link, newest first. */
export const referredAccountsQueryOptions = (page: number) =>
  queryOptions({
    queryKey: ["referredAccounts", page],
    queryFn: ({ signal }) =>
      apiClient
        .get<ReferredAccountsResp>("/api/a/referrals/accounts", {
          params: {
            limit: REFERRED_ACCOUNTS_PAGE_SIZE,
            offset: page * REFERRED_ACCOUNTS_PAGE_SIZE,
          },
          signal,
        })
        .then((res) => res.data),
  });

/**
 * The same list loaded a page at a time as it scrolls, for native. Its key
 * can't collide with the paged one's: a page number is never a string.
 *
 * The next offset is what has been loaded so far. It stops at the total, or
 * at an empty page when deletions have left the total higher than the rows.
 */
export const referredAccountsInfiniteQueryOptions = infiniteQueryOptions({
  queryKey: ["referredAccounts", "infinite"],
  queryFn: ({ pageParam, signal }) =>
    apiClient
      .get<ReferredAccountsResp>("/api/a/referrals/accounts", {
        params: { limit: REFERRED_ACCOUNTS_PAGE_SIZE, offset: pageParam },
        signal,
      })
      .then((res) => res.data),
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages) => {
    const loaded = allPages.reduce((sum, page) => sum + page.accounts.length, 0);
    return lastPage.accounts.length > 0 && loaded < lastPage.total ? loaded : undefined;
  },
});
